/**
 * SPlanValve — animated SVG showing two independent 2-port zone valves.
 * Demonstrates independent CH/DHW control.
 */

import { useState, useEffect } from 'react';

export function SPlanValve() {
  const [chOpen, setChOpen] = useState(false);
  const [hwOpen, setHwOpen] = useState(true);

  useEffect(() => {
    const states = [
      { ch: false, hw: true },
      { ch: true, hw: true },
      { ch: true, hw: false },
      { ch: true, hw: true },
    ];
    let idx = 0;
    const timer = setInterval(() => {
      idx = (idx + 1) % states.length;
      setChOpen(states[idx].ch);
      setHwOpen(states[idx].hw);
    }, 2000);
    return () => clearInterval(timer);
  }, []);

  const valveColor = (open: boolean) => open ? '#22c55e' : '#ef4444';
  const valveLabel = (open: boolean) => open ? 'OPEN' : 'CLOSED';

  return (
    <figure style={{ margin: 0 }}>
      <svg viewBox="0 0 440 240" width="100%" role="img" aria-label="S-plan independent zone valves">
        <title>S-plan with two independent 2-port zone valves</title>
        <defs>
          <style>{`
            .sp-pipe { stroke: #64748b; stroke-width: 3; fill: none; }
            .sp-flow { stroke: #3b82f6; stroke-width: 3; fill: none; opacity: 0.6; stroke-dasharray: 8,6; animation: sp-dash 1s linear infinite; }
            .sp-label { font-family: system-ui, sans-serif; font-size: 12px; fill: #475569; text-anchor: middle; }
            .sp-title { font-family: system-ui, sans-serif; font-size: 11px; fill: #1e293b; font-weight: 600; text-anchor: middle; }
            .sp-valve { stroke: #334155; stroke-width: 2; transition: fill 0.3s; }
            .sp-status { font-family: system-ui, sans-serif; font-size: 10px; font-weight: 600; text-anchor: middle; transition: fill 0.3s; }
            @keyframes sp-dash { to { stroke-dashoffset: -14; } }
          `}</style>
        </defs>

        {/* Boiler */}
        <rect x="180" y="10" width="80" height="40" rx="6" fill="#e2e8f0" stroke="#475569" strokeWidth="2" />
        <text x="220" y="35" className="sp-title">Boiler</text>

        {/* Main flow down from boiler */}
        <line x1="220" y1="50" x2="220" y2="90" className="sp-pipe" />
        <line x1="220" y1="50" x2="220" y2="90" className="sp-flow" />

        {/* Split to two branches */}
        <line x1="220" y1="90" x2="120" y2="90" className="sp-pipe" />
        <line x1="220" y1="90" x2="320" y2="90" className="sp-pipe" />

        {/* HW branch */}
        <line x1="120" y1="90" x2="120" y2="120" className="sp-pipe" />
        {hwOpen && <line x1="120" y1="90" x2="120" y2="120" className="sp-flow" />}

        {/* HW valve */}
        <rect x="105" y="120" width="30" height="20" rx="4" className="sp-valve" fill={valveColor(hwOpen)} />
        <text x="120" y="155" className="sp-status" fill={valveColor(hwOpen)}>{valveLabel(hwOpen)}</text>

        {/* HW circuit */}
        <line x1="120" y1="140" x2="120" y2="190" className="sp-pipe" />
        {hwOpen && <line x1="120" y1="140" x2="120" y2="190" className="sp-flow" />}
        <rect x="90" y="190" width="60" height="30" rx="6" fill="#dbeafe" stroke="#3b82f6" strokeWidth="1.5" />
        <text x="120" y="210" className="sp-title">Cylinder</text>

        {/* CH branch */}
        <line x1="320" y1="90" x2="320" y2="120" className="sp-pipe" />
        {chOpen && <line x1="320" y1="90" x2="320" y2="120" className="sp-flow" />}

        {/* CH valve */}
        <rect x="305" y="120" width="30" height="20" rx="4" className="sp-valve" fill={valveColor(chOpen)} />
        <text x="320" y="155" className="sp-status" fill={valveColor(chOpen)}>{valveLabel(chOpen)}</text>

        {/* CH circuit */}
        <line x1="320" y1="140" x2="320" y2="190" className="sp-pipe" />
        {chOpen && <line x1="320" y1="140" x2="320" y2="190" className="sp-flow" />}
        <rect x="290" y="190" width="60" height="30" rx="6" fill="#fef3c7" stroke="#f59e0b" strokeWidth="1.5" />
        <text x="320" y="210" className="sp-title">Radiators</text>

        {/* Labels */}
        <text x="120" y="80" className="sp-label">Hot water zone</text>
        <text x="320" y="80" className="sp-label">Heating zone</text>
      </svg>
      <figcaption style={{ textAlign: 'center', fontSize: '0.85rem', color: '#64748b', marginTop: 4 }}>
        Two independent 2-port valves — each zone controlled separately.
      </figcaption>
    </figure>
  );
}
